// src/components/Stats.jsx
import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { motion } from "framer-motion";
import { Users, Folder, Calendar } from "lucide-react";

/* ------------------------------
   CONTAINER
-------------------------------- */
const Container = styled.section`
  background: ${(p) =>
    p.theme.gradients?.bgSection ||
    `linear-gradient(180deg, ${p.theme.colors.bgDarker}, ${p.theme.colors.bgDark})`};
  color: ${(p) => p.theme.colors.text};
  padding: 60px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

/* ------------------------------
   HEADER
-------------------------------- */
const Header = styled.div`
  max-width: 1100px;
  margin: 0 auto 32px auto;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 2.2rem;
  font-weight: 900;
`;

const Lead = styled.p`
  margin: 0;
  color: ${(p) => p.theme.colors.muted};
  font-size: 1.05rem;
`;

/* ------------------------------
   GRID
-------------------------------- */
const Grid = styled.div`
  max-width: 1200px;
  margin: 40px auto 0;
  display: grid;
  gap: 26px;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  justify-items: center;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

/* ------------------------------
   CARD
-------------------------------- */
const StatCard = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.55);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

/* ------------------------------
   CARD TOP
-------------------------------- */
const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  justify-content: center;
  width: 100%;
`;

const IconWrap = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: ${(p) => p.styleBg};
  color: ${(p) => p.theme.colors.bgDark};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  flex-shrink: 0;
`;

/* ------------------------------
   ROLLING DIGITS
-------------------------------- */
const BigNumberWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const DigitColumn = styled.div`
  overflow: hidden;
  /* height and width will be set inline to match JS slotHeight */
  width: 26px;

  @media (max-width: 600px) {
    width: 20px;
  }
`;

const DigitStack = styled(motion.div)`
  display: flex;
  flex-direction: column;
  font-size: 2rem;
  font-weight: 900;
  line-height: 48px;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 1.4rem;
    line-height: 34px;
  }
`;

const StaticChar = styled.span`
  font-size: 2rem;
  font-weight: 900;
  line-height: 48px;
  color: ${(p) => p.theme.colors.text};
  display: inline-block;

  @media (max-width: 600px) {
    font-size: 1.4rem;
    line-height: 34px;
  }
`;

const Meta = styled.div`
  color: ${(p) => p.theme.colors.muted};
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
`;

/* ------------------------------
   CIRCLE
-------------------------------- */
const CircleWrap = styled.div`
  width: 130px;
  height: 130px;
  display: grid;
  place-items: center;
`;

const CircleSvg = styled.svg`
  transform: rotate(-90deg);
`;

/* ------------------------------
   FORMATTERS
-------------------------------- */
const formatResponsiveNumber = (num, isMobile) => {
  if (!isMobile) {
    // FULL FORMAT on big screens
    return new Intl.NumberFormat("en-IN").format(num);
  }

  // COMPACT on mobile
  if (num >= 1_00_00_000) {
    return (num / 1_00_00_000).toFixed(1).replace(/\.0$/, "") + "Cr";
  }
  if (num >= 1_00_000) {
    return (num / 1_00_000).toFixed(1).replace(/\.0$/, "") + "L";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return String(num);
};

/* ------------------------------
   RollingDigits Component
   - Uses a responsive slotHeight that updates on resize
   - Only digits animate; suffixes stay static
-------------------------------- */
function RollingDigits({ value, prefix = "₹" }) {
  // track window width so slotHeight matches CSS media queries
  const [, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 600;
  const slotHeight = isMobile ? 34 : 48;
  const digitWidth = isMobile ? 20 : 26;
  const formatted = formatResponsiveNumber(value, isMobile);
  const chars = Array.from(formatted);

  return (
    <BigNumberWrap>
      {prefix && <StaticChar style={{ marginRight: 4 }}>{prefix}</StaticChar>}

      {chars.map((ch, idx) => {
        // Non-digit (including dot and letters) -> static
        if (!/^\d$/.test(ch)) {
          return (
            <StaticChar key={idx} style={{ marginLeft: 2 }}>
              {ch}
            </StaticChar>
          );
        }

        // Digit: animate via vertical stack
        const digit = Number(ch);

        return (
          <DigitColumn
            key={idx}
            style={{ height: slotHeight, width: digitWidth }}
            aria-hidden
          >
            <DigitStack
              initial={{ y: -9 * slotHeight }}
              animate={{ y: -digit * slotHeight }}
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 26,
                delay: idx * 0.08,
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <div
                  key={d}
                  style={{
                    height: slotHeight,
                    lineHeight: `${slotHeight}px`,
                    textAlign: "center",
                  }}
                >
                  {d}
                </div>
              ))}
            </DigitStack>
          </DigitColumn>
        );
      })}
    </BigNumberWrap>
  );
}

/* ------------------------------
   Count Animation Hook
-------------------------------- */
function useCountTo(target, duration = 1400, deps = []) {
  const [val, setVal] = React.useState(0);
  const ref = React.useRef(null);

  useEffect(() => {
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      // cubic ease-out-ish
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(target * eased));

      if (progress < 1) {
        ref.current = requestAnimationFrame(animate);
      }
    };

    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return val;
}

/* ------------------------------
   MAIN COMPONENT
-------------------------------- */
export default function Stats() {
  const theme = useTheme();

  const totalCollectionTarget = 70 * 2_000_000;
  const totalCasesTarget = 70 * 6000;
  const avgRecoveryRateTarget = 78;
  const yearsActiveTarget = new Date().getFullYear() - 2020 + 1;

  const totalCollection = useCountTo(totalCollectionTarget, 1400, []);
  const totalCases = useCountTo(totalCasesTarget, 1400, []);
  const avgRecoveryRate = useCountTo(avgRecoveryRateTarget, 1200, []);
  const yearsActive = useCountTo(yearsActiveTarget, 900, []);

  const circumference = 2 * Math.PI * 50;
  const offset = ((100 - avgRecoveryRate) / 100) * circumference;

  const cardVariant = {
    hidden: { opacity: 0, y: 12 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.5 },
    }),
  };

  return (
    <Container id="stats">
      <Header>
        <Title>Key Performance</Title>
        <Lead>
          Transparent, measurable results delivered consistently since 2020.
        </Lead>
      </Header>

      <Grid>
        {/* TOTAL COLLECTION */}
        <StatCard
          custom={0}
          initial="hidden"
          animate="visible"
          variants={cardVariant}
        >
          <CardTop>
            <IconWrap styleBg={theme.gradients.primary}>
              <Users size={28} strokeWidth={1.6} />
            </IconWrap>

            <div>
              <RollingDigits value={totalCollection} prefix="₹" />
              <Meta>Total collections till now</Meta>
            </div>
          </CardTop>
        </StatCard>

        {/* TOTAL CASES */}
        <StatCard
          custom={1}
          initial="hidden"
          animate="visible"
          variants={cardVariant}
        >
          <CardTop>
            <IconWrap styleBg={theme.gradients.accent}>
              <Folder size={28} strokeWidth={1.6} />
            </IconWrap>

            <div>
              <RollingDigits value={totalCases} prefix="" />
              <Meta>Total loan cases handled</Meta>
            </div>
          </CardTop>
        </StatCard>

        {/* RECOVERY RATE */}
        <StatCard
          custom={2}
          initial="hidden"
          animate="visible"
          variants={cardVariant}
        >
          <CardTop>
            <CircleWrap>
              <CircleSvg width="130" height="130" viewBox="0 0 130 130">
                <circle
                  cx="65"
                  cy="65"
                  r="50"
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="12"
                  fill="none"
                />

                <motion.circle
                  cx="65"
                  cy="65"
                  r="50"
                  stroke="url(#grad)"
                  strokeWidth="12"
                  strokeLinecap="round"
                  fill="none"
                  strokeDasharray={circumference}
                  initial={{ strokeDashoffset: circumference }}
                  animate={{ strokeDashoffset: offset }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />

                <defs>
                  <linearGradient id="grad" x1="0" x2="1">
                    <stop offset="0%" stopColor={theme.colors.accent} />
                    <stop offset="100%" stopColor={theme.colors.accentLight} />
                  </linearGradient>
                </defs>
              </CircleSvg>
            </CircleWrap>

            <div>
              <StaticChar>{avgRecoveryRate}%</StaticChar>
              <Meta>Average recovery rate</Meta>
            </div>
          </CardTop>
        </StatCard>

        {/* YEARS ACTIVE */}
        <StatCard
          custom={3}
          initial="hidden"
          animate="visible"
          variants={cardVariant}
        >
          <CardTop>
            <IconWrap styleBg={theme.gradients.cta}>
              <Calendar size={28} strokeWidth={1.6} />
            </IconWrap>

            <div>
              <StaticChar>{yearsActive} yrs</StaticChar>
              <Meta>Industry experience</Meta>
            </div>
          </CardTop>
        </StatCard>
      </Grid>
    </Container>
  );
}
